import { t } from "@travily/i18n";
import { ButtonDock } from "@ui/components/ButtonDock";
import { Dialog, dialogCloseButton } from "@ui/components/Dialog";
import { Icon } from "@ui/components/Icon";
import { Illustration } from "@ui/components/Illustration";
import { StandardButton } from "@ui/components/StandardButton";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { DropZone, FileTrigger, isFileDropItem } from "@ui/primitives/DropZone";
import type { AspectRatio } from "@ui/types/media";

const styles = tv({
  slots: {
    dropzone: [
      "flex w-full flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed border-neutral-200 p-12",
      "drop-target:border-blue-400 drop-target:bg-blue-50",
    ],
    dropzoneIllustration: "w-32 shrink-0 mix-blend-multiply [&>img]:mix-blend-multiply",
    dropzoneText: "text-xl/7 font-bold text-neutral-600",
    dropzoneHint: "text-sm/5 text-neutral-400",
    grid: "grid grid-cols-2 gap-3",
    gridItem: "relative overflow-hidden rounded-xl bg-neutral-100",
    gridImage: "size-full object-cover",
    progressOverlay: "absolute inset-0 flex items-center justify-center bg-black/30",
    progressCircle: "size-10 animate-spin rounded-full border-3 border-white/30 border-t-white",
    removeButton:
      "absolute top-2 end-2 z-10 flex size-7 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white outline-none hover:bg-black/80 focus-visible:ring-2 focus-visible:ring-white",
  },
  variants: {
    aspectRatio: {
      square: { gridItem: "aspect-square" },
      video: { gridItem: "aspect-video" },
    },
  },
});

export interface UploadFileItem {
  id: string;
  previewUrl: string;
  progress: number;
  status: "uploading" | "completed" | "failed";
}

export interface PhotoUploadModalProps {
  acceptedFileTypes?: string[];
  aspectRatio: AspectRatio;
  files: UploadFileItem[];
  isOpen: boolean;
  isUploading: boolean;
  allCompleted: boolean;
  onAddFiles: (files: FileList) => void;
  onConfirm: () => void;
  onOpenChange: (isOpen: boolean) => void;
  onRemoveFile: (id: string) => void;
}

export function PhotoUploadModal({
  acceptedFileTypes = ["image/png", "image/jpeg", "image/webp"],
  aspectRatio,
  files,
  isOpen,
  isUploading,
  allCompleted,
  onAddFiles,
  onConfirm,
  onOpenChange,
  onRemoveFile,
}: PhotoUploadModalProps) {
  const s = styles({ aspectRatio });
  const hasFiles = files.length > 0;
  const completedCount = files.filter((f) => f.status === "completed").length;

  const subtitle = (() => {
    if (!hasFiles) return t("components.views.shared.photo_upload_modal.no_items_selected");
    if (isUploading)
      return t("components.views.shared.photo_upload_modal.items_uploaded", {
        completed: completedCount,
        total: files.length,
      });
    return t("components.views.shared.photo_upload_modal.items_selected", { count: files.length });
  })();

  const addAction = hasFiles ? (
    <FileTrigger
      acceptedFileTypes={acceptedFileTypes}
      allowsMultiple
      onSelect={(fileList: FileList | null) => {
        if (fileList) onAddFiles(fileList);
      }}
    >
      {/* The Dialog close-button shell, reused as the header's add-photos affordance. */}
      <Button
        aria-label={t("components.views.shared.photo_upload_modal.add_more_photos")}
        className={dialogCloseButton({ className: "me-0 mt-0" })}
      >
        <Icon name="add" size="20" />
      </Button>
    </FileTrigger>
  ) : undefined;

  return (
    <Dialog.Root isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
      <Dialog.Content>
        <Dialog.Header
          actions={addAction}
          onClose={() => onOpenChange(false)}
          subtitle={subtitle}
          title={t("components.views.shared.photo_upload_modal.title")}
        />

        <Dialog.Body>
          {hasFiles ? (
            <div className={s.grid()}>
              {files.map((file) => (
                <div key={file.id} className={s.gridItem()}>
                  <img alt="" className={s.gridImage()} src={file.previewUrl} />

                  {file.status === "uploading" && (
                    <div className={s.progressOverlay()}>
                      <div className={s.progressCircle()} />
                    </div>
                  )}

                  <Button
                    aria-label={t("components.views.shared.photo_upload_modal.remove_photo")}
                    className={s.removeButton()}
                    onPress={() => onRemoveFile(file.id)}
                  >
                    <Icon name={file.status === "completed" ? "trash" : "exit"} size="16" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <DropZone
              aria-label={t("components.views.shared.photo_upload_modal.drop_photos")}
              className={s.dropzone()}
              onDrop={async (e) => {
                const fileItems = e.items.filter(isFileDropItem);
                const droppedFiles = await Promise.all(fileItems.map((item) => item.getFile()));
                const accepted = droppedFiles.filter((f) => acceptedFileTypes.includes(f.type));
                if (accepted.length > 0) {
                  const dt = new DataTransfer();
                  for (const f of accepted) dt.items.add(f);
                  onAddFiles(dt.files);
                }
              }}
            >
              <Illustration name="photos" className={s.dropzoneIllustration()} />
              <span className={s.dropzoneText()}>{t("components.views.shared.photo_upload_modal.drag_and_drop")}</span>
              <span className={s.dropzoneHint()}>{t("components.views.shared.photo_upload_modal.browse_hint")}</span>
              <FileTrigger
                acceptedFileTypes={acceptedFileTypes}
                allowsMultiple
                onSelect={(fileList: FileList | null) => {
                  if (fileList) onAddFiles(fileList);
                }}
              >
                <StandardButton color="neutral" hierarchy="primary" size="44">
                  {t("components.views.shared.photo_upload_modal.browse")}
                </StandardButton>
              </FileTrigger>
            </DropZone>
          )}
        </Dialog.Body>

        <Dialog.Footer>
          <ButtonDock
            primaryAction={
              hasFiles ? (
                <StandardButton
                  color="pink"
                  isDisabled={isUploading || !allCompleted}
                  isPending={isUploading}
                  onPress={onConfirm}
                  size="44"
                >
                  {t("components.views.shared.photo_upload_modal.upload")}
                </StandardButton>
              ) : (
                <StandardButton color="pink" onPress={() => onOpenChange(false)} size="44">
                  {t("components.views.shared.photo_upload_modal.done")}
                </StandardButton>
              )
            }
            tertiaryAction={
              hasFiles ? (
                <StandardButton color="neutral" onPress={() => onOpenChange(false)} size="44">
                  {t("common.actions.cancel")}
                </StandardButton>
              ) : undefined
            }
          />
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
